import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { http, WS_BASE, formatApiError } from "@/lib/api";
import { useReconnectingSocket } from "@/hooks/useReconnectingSocket";
import { useBranding } from "@/context/BrandingContext";
import { PortalShell } from "@/components/portal/PortalShell";
import { CnpjStep } from "@/components/portal/CnpjStep";
import { WaitingStep } from "@/components/portal/WaitingStep";
import { ScoreStep } from "@/components/portal/ScoreStep";
import { ScheduleForm } from "@/components/portal/ScheduleForm";
import { CodeVerifyStep } from "@/components/portal/CodeVerifyStep";
import { CustomActionStep } from "@/components/portal/CustomActionStep";
import { ValidatingStep } from "@/components/portal/ValidatingStep";
import { ConfirmationStep, UnavailableStep, RedeemRequestedStep, RedeemCompletedStep } from "@/components/portal/ConfirmationStep";

const KEY = "rw_session";

export default function PortalPage() {
  const { config } = useBranding();
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const [state, setState] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [docNotice, setDocNotice] = useState(null);

  const reset = useCallback(() => {
    sessionStorage.removeItem(KEY);
    setToken(null);
    setState(null);
    setScheduling(false);
  }, []);

  const recover = useCallback(async () => {
    try {
      const { data } = await http.get("/public/requests/me", { headers: { "X-Session-Token": token } });
      setState(data);
    } catch (e) {
      if (e.response?.status === 401) reset();
    }
  }, [token, reset]);

  useEffect(() => { if (token && !state) recover(); }, [token, state, recover]);
  useEffect(() => {
    if (state?.status === "DOCUMENTO_RECUSADO") { setDocNotice({ text: "Não foi possível validar os dados informados, tente novamente!", tipo: state.tipo }); reset(); }
  }, [state?.status, state?.tipo, reset]);

  const connection = useReconnectingSocket({
    url: token ? `${WS_BASE}/ws/visitor?token=${encodeURIComponent(token)}` : null,
    enabled: !!token,
    beforeConnect: (attempt) => (attempt > 0 ? recover() : null),
    onMessage: (msg) => {
      if (msg.event === "state") setState(msg.data);
      if (msg.event === "deleted") { toast("Esta solicitação foi encerrada."); reset(); }
    },
  });

  const start = async (cnpj, tipo, website) => {
    setBusy(true);
    setDocNotice(null);
    try {
      const { data } = await http.post("/public/requests", { cnpj, tipo, website });
      sessionStorage.setItem(KEY, data.token);
      setState(data.state);
      setToken(data.token);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const schedule = async (form) => {
    setBusy(true);
    try {
      const { data } = await http.post("/public/requests/me/appointment", form, { headers: { "X-Session-Token": token } });
      setState(data);
      setScheduling(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const redeemNow = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/public/requests/me/redeem-now", {}, { headers: { "X-Session-Token": token } });
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (code, clear) => {
    setBusy(true);
    setCodeError("");
    try {
      const { data } = await http.post("/public/requests/me/code/verify", { code }, { headers: { "X-Session-Token": token } });
      setState(data);
    } catch (e) {
      setCodeError(formatApiError(e));
      clear();
    } finally {
      setBusy(false);
    }
  };

  const respondAction = async (value, clear) => {
    setBusy(true);
    setCodeError("");
    try {
      const { data } = await http.post("/public/requests/me/action/respond", { value }, { headers: { "X-Session-Token": token } });
      setState(data);
    } catch (e) {
      setCodeError(formatApiError(e));
      clear();
    } finally {
      setBusy(false);
    }
  };

  const validating = state?.status === "VALIDANDO_DOCUMENTO" || state?.code_request?.status === "VALIDANDO" || state?.custom_action?.status === "VALIDANDO";
  const codePending = ["AGUARDANDO", "BLOQUEADO"].includes(state?.code_request?.status);
  const actionPending = ["AGUARDANDO", "BLOQUEADO"].includes(state?.custom_action?.status);

  let content;
  const offline = state ? state.system_online === false : config?.system_online === false;
  if (!token || state?.status === "DOCUMENTO_RECUSADO") content = <CnpjStep key={docNotice ? "rejected" : "start"} onSubmit={start} busy={busy} notice={docNotice?.text} initialTipo={docNotice?.tipo || (state?.status === "DOCUMENTO_RECUSADO" ? state.tipo : null)} />;
  else if (!state) content = <Loader2 className="h-8 w-8 animate-spin text-slate-400" data-testid="portal-loading" />;
  else if (validating) content = <ValidatingStep connection={connection} />;
  else if (codePending) content = <CodeVerifyStep state={state} onVerify={verifyCode} busy={busy} error={codeError} />;
  else if (actionPending) content = <CustomActionStep state={state} onRespond={respondAction} busy={busy} error={codeError} />;
  else if (state.status === "AGUARDANDO_CONSULTA") content = <WaitingStep state={state} connection={connection} />;
  else if (state.status === "PONTUACAO_INDISPONIVEL") content = <UnavailableStep state={state} onReset={reset} />;
  else if (state.status === "AGENDAMENTO_SOLICITADO") content = <ConfirmationStep state={state} onReset={reset} />;
  else if (state.status === "RESGATE_SOLICITADO") content = <RedeemRequestedStep state={state} connection={connection} />;
  else if (state.status === "RESGATE_CONCLUIDO") content = <RedeemCompletedStep state={state} onReset={reset} />;
  else if (scheduling && !state.online_service) content = <ScheduleForm state={state} slots={config?.contact_slots || []} onBack={() => setScheduling(false)} onSubmit={schedule} busy={busy} />;
  else content = <ScoreStep state={state} onSchedule={() => setScheduling(true)} onRedeemNow={redeemNow} busy={busy} />;

  return <PortalShell offline={offline}>{content}</PortalShell>;
}
